import { ChevronDownIcon } from '@radix-ui/react-icons'
import * as Select from '@radix-ui/react-select'
import { useTranslation } from 'react-i18next'

import { WidgetModal } from '@/components/Modal/WidgetModal'
import { TransactionType, useBuySellStore } from '@/reducers/transactionReducer'
import {
  CoinOption,
  EFT_PAYMENT_OPTION,
  getPaymentOptions,
  PaymentTypes,
  WIRE_PAYMENT_OPTION,
} from '@/utils/currencies'
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
import { useLimits } from '@/hooks/useLimits'
import { CurrencyInput } from './CurrencyInput'
import { resolveSelfServeMaxCad, resolveSelfServeMaxStableCoin } from './utils'

type SelectValueProps = {
  option: CoinOption
  disabled?: boolean
} & Select.SelectValueProps

const SelectValue = ({ option, disabled }: SelectValueProps) => {
  return (
    <Select.Trigger
      aria-label={option.name}
      className={`inline-flex items-start justify-center text-base leading-none min-h-[35px] gap-[5px] outline-none focus:bg-neutral-200 dark:focus:bg-slate-600 border-rock border-b-2 p-1 ${
        disabled ? 'pr-4' : ''
      }`}
      disabled={disabled}
      data-testid="text-select-trigger"
    >
      <>
        <Select.Value aria-label={option.name}>
          <div className="flex">
            {option.image && (
              <div
                className="mr-1 md:mr-2 w-15"
                style={{ marginBottom: '-1px' }}
                data-testid="text-select-image-container"
              >
                <img
                  src={option.image}
                  height={40}
                  width={40}
                  data-testid="text-select-image"
                />
              </div>
            )}
            <div
              className="text-left flex flex-col justify-end mt-2"
              data-testid="text-select-text"
            >
              <div className="font-normal" data-testid="text-select-name">
                {option?.name}
              </div>
              <div
                className="text-sm text-pebble dark:text-white"
                data-testid="text-select-description"
              >
                {option.shortDescription ?? option.description}
              </div>
            </div>
          </div>
        </Select.Value>
        {!disabled && (
          <Select.Icon>
            <ChevronDownIcon />
          </Select.Icon>
        )}
      </>
    </Select.Trigger>
  )
}

type CoinSelectProps = {
  options: CoinOption[]
  disabled?: boolean
} & Select.SelectProps

export const CoinSelect = ({
  options,
  disabled,
  ...restProps
}: CoinSelectProps) => {
  const selectedOption = options.find(
    (option) => option.value === restProps.value
  )
  return (
    <Select.Root
      {...restProps}
      disabled={disabled}
      data-testid="select-coin-select"
    >
      <SelectValue option={selectedOption as CoinOption} disabled={disabled} />
      <Select.Portal>
        <Select.Content
          position="popper"
          sideOffset={5}
          className="overflow-hidden w-[var(--radix-select-trigger-width)] bg-snow dark:bg-gravel border border-gravel dark:border-snow"
          data-testid="text-select-dropdown"
        >
          <Select.Viewport>
            {options.map((coinOption) => {
              return (
                <Select.Item
                  className="text-[13px] outline-none focus:bg-neutral-200 dark:focus:bg-slate-600 leading-none p-2 hover:bg-neutral-300 hover:dark:bg-pebble"
                  key={coinOption.value}
                  value={coinOption.value}
                  data-testid={`coin-select-option-${coinOption.value}`}
                >
                  <div className="flex items-center">
                    {coinOption.image && (
                      <div className="mr-2 mt-1">
                        <img src={coinOption.image} height={40} width={40} />
                      </div>
                    )}
                    <div>
                      <div className="font-normal">{coinOption.name}</div>
                    </div>
                  </div>
                </Select.Item>
              )
            })}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  )
}

export const FiatSelect = ({
  options,
  disabled,
  ...restProps
}: CoinSelectProps) => {
  const selectedOption = options.find(
    (option) => option.value === restProps.value
  )
  return (
    <Select.Root
      {...restProps}
      disabled={disabled}
      data-testid="container-transaction-fiat-select"
    >
      <SelectValue
        style={{ fontWeight: 'bold' }}
        option={selectedOption as CoinOption}
        disabled={disabled}
      />
      <Select.Portal>
        <Select.Content
          position="popper"
          sideOffset={5}
          className="overflow-hidden w-[var(--radix-select-trigger-width)] bg-snow dark:bg-gravel border border-gravel dark:border-snow"
          data-testid="text-select-dropdown"
        >
          <Select.Viewport>
            {options.map((coinOption) => {
              return (
                <Select.Item
                  className="text-[13px] outline-none focus:bg-neutral-200 dark:focus:bg-slate-600 leading-none p-2 hover:bg-neutral-300 hover:dark:bg-pebble"
                  key={coinOption.value}
                  value={coinOption.value}
                  data-testid={`fiat-select-option-${coinOption.value}`}
                >
                  <div className="flex">
                    {coinOption.image && (
                      <div className="mr-2 mt-1 ">
                        <img src={coinOption.image} height={40} width={40} />
                      </div>
                    )}
                    <div>
                      <div className="font-normal">{coinOption.name}</div>
                      <div className="text-pebble">
                        {coinOption.description}
                      </div>
                    </div>
                  </div>
                </Select.Item>
              )
            })}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  )
}

type RowProps = {
  onAmountChange?: () => void
  hideAmount?: boolean
}

export const StableCoinRow = ({
  onAmountChange,
  hideAmount = false,
}: RowProps = {}) => {
  const { t } = useTranslation()
  const transactionType = useBuySellStore.use.transactionType()
  const coinAmount = useBuySellStore.use.stableCoinAmount()
  const stableCoin = useBuySellStore.use.stableCoin()
  const setStableCoinAmount = useBuySellStore.use.setStableCoinAmountV2()
  const inputsDisabled = useBuySellStore.use.inputsDisabled()

  const { enableOtc, enable25kLimitIncrease } = useFeatureFlags()
  const limits = useLimits()

  const maxSelfServeStableCoin = resolveSelfServeMaxStableCoin(
    limits,
    stableCoin,
    enable25kLimitIncrease
  )
  const maxAmount = enableOtc ? undefined : maxSelfServeStableCoin

  const handleAmountChange = (value: number) => {
    onAmountChange?.()
    setStableCoinAmount(value)
  }

  const urlParams = new URLSearchParams(window.location.search)
  const isNetworkLocked =
    sessionStorage.getItem('lockNetwork') === 'true' ||
    urlParams.get('lockNetwork') === 'true' ||
    urlParams.get('autoConnect') === 'true'

  const destinationLabel =
    transactionType === TransactionType.BUY
      ? t('coinChooser.receive')
      : t('coinChooser.pay')

  if (hideAmount) {
    return (
      <div className="flex flex-col gap-3" data-testid="text-coin-row">
        <label className="uppercase" data-testid="text-coin-destination-label">
          {destinationLabel}
        </label>
        <div
          className="border-rock border-b-2 w-full"
          data-testid="text-coin-modal-container"
        >
          <WidgetModal disableNetworkSelect={isNetworkLocked} fullWidth />
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-end" data-testid="text-coin-row">
      <div className="flex-grow">
        <CurrencyInput
          id="stablecoin"
          label={destinationLabel}
          amount={coinAmount}
          setAmount={handleAmountChange}
          decimal={2}
          max={maxAmount}
          min={0}
          autoFocus={transactionType === TransactionType.SELL}
          disabled={inputsDisabled}
        />
      </div>
      <div
        className="flex-shrink min-w-min flex items-end border-rock border-b-2"
        data-testid="text-coin-modal-container"
      >
        <WidgetModal disableNetworkSelect={isNetworkLocked} />
      </div>
    </div>
  )
}

export const FiatCoinRow = ({
  onAmountChange,
  hideAmount = false,
}: RowProps = {}) => {
  const { t } = useTranslation()
  const transactionType = useBuySellStore.use.transactionType()
  const fiatAmount = useBuySellStore.use.fiatAmount()
  const setFiatAmount = useBuySellStore.use.setFiatAmountV2()
  const requestType = useBuySellStore.use.requestType()
  const setRequestType = useBuySellStore.use.setRequestType()
  const inputsDisabled = useBuySellStore.use.inputsDisabled()

  const { enableOtc, enable25kLimitIncrease } = useFeatureFlags()
  const limits = useLimits()

  const maxSelfServeCad = resolveSelfServeMaxCad(limits, enable25kLimitIncrease)
  const maxAmount = enableOtc ? undefined : maxSelfServeCad

  const isOtcOrder = enableOtc && fiatAmount > maxSelfServeCad

  const handleAmountChange = (value: number) => {
    onAmountChange?.()
    setFiatAmount(value)
  }

  const destinationLabel =
    transactionType === TransactionType.BUY
      ? t('coinChooser.pay')
      : t('coinChooser.receive')

  const cadPaymentOption =
    transactionType === TransactionType.SELL
      ? EFT_PAYMENT_OPTION
      : WIRE_PAYMENT_OPTION

  if (hideAmount) {
    return (
      <div className="flex flex-col gap-3" data-testid="text-coin-row">
        <label className="uppercase" data-testid="text-coin-destination-label">
          {destinationLabel}
        </label>
        <div
          className="border-rock border-b-2 w-full flex items-center pb-1"
          data-testid="text-fiat-display"
        >
          {cadPaymentOption.image && (
            <div className="mr-1 md:mr-2 w-15" style={{ marginBottom: '-1px' }}>
              <img src={cadPaymentOption.image} height={40} width={40} />
            </div>
          )}
          <div className="text-left flex flex-col justify-center">
            <div className="font-normal">{cadPaymentOption.name}</div>
            <div className="text-sm text-pebble dark:text-white">
              {cadPaymentOption.shortDescription}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-end" data-testid="text-coin-row">
      <div className="flex-grow ">
        <CurrencyInput
          id="fiat"
          label={
            transactionType === TransactionType.BUY
              ? t('coinChooser.pay')
              : t('coinChooser.receive')
          }
          amount={fiatAmount}
          setAmount={handleAmountChange}
          max={maxAmount}
          min={0}
          autoFocus={transactionType === TransactionType.BUY}
          disabled={inputsDisabled}
        />
      </div>
      <div
        className="flex-shrink min-w-min "
        style={{ marginBottom: '-2px', marginRight: '6px' }}
        data-testid="text-select-container"
      >
        <FiatSelect
          name="requestType"
          disabled={true}
          options={getPaymentOptions(true)}
          value={isOtcOrder ? PaymentTypes.WIRE : requestType}
          onValueChange={setRequestType}
        />
      </div>
    </div>
  )
}

/*
 * Code Improvement Suggestions:
 *
 * 1. The component contains duplicated code in CoinSelect and FiatSelect - consider creating a common base component
 * 2. Excessive nesting of divs could be simplified for better performance and readability
 * 3. Consider using React.memo for optimization with proper dependency arrays
 * 4. Inline styles should be moved to styled components or CSS classes
 * 5. Some hardcoded values like "10010" should be moved to constants
 * 6. The mapping function for rendering options could be extracted for reusability
 * 7. Add error handling for cases where selectedOption is undefined
 * 8. Consider implementing virtualization for long option lists
 */
